/**
 * The felanmälan auto-assignment setting (`global/autoAssignment/issues`,
 * readable by listed users), read once per session and shared by every
 * issue card and detail that derives "Väntar på förvaltare" from it.
 */
import { useEffect, useState } from 'react';
import { get, getDatabase, ref } from 'firebase/database';
import { IssueAutoAssignment, ISSUE_AUTO_ASSIGNMENT_PATH, parseIssueAutoAssignment } from '../../types/autoAssignment';

let cached: Promise<IssueAutoAssignment | null> | null = null;
const listeners = new Set<(setting: IssueAutoAssignment | null) => void>();

const load = (): Promise<IssueAutoAssignment | null> => {
    if (!cached) {
        cached = get(ref(getDatabase(), ISSUE_AUTO_ASSIGNMENT_PATH))
            .then((snapshot) => parseIssueAutoAssignment(snapshot.val()))
            .catch((err) => {
                console.error('Failed to read auto-assignment setting', err);
                cached = null;
                return null;
            });
    }
    return cached;
};

/** Replace the cached setting (after a save) and update every mounted reader. */
export const setCachedIssueAutoAssignment = (setting: IssueAutoAssignment | null): void => {
    cached = Promise.resolve(setting);
    listeners.forEach((listener) => listener(setting));
};

/** Test hook: forget the cached read. */
export const resetIssueAutoAssignmentCache = (): void => {
    cached = null;
};

export const useIssueAutoAssignment = (): { setting: IssueAutoAssignment | null; loading: boolean } => {
    const [setting, setSetting] = useState<IssueAutoAssignment | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const listener = (next: IssueAutoAssignment | null) => { if (mounted) setSetting(next); };
        listeners.add(listener);
        void load().then((s) => {
            if (!mounted) return;
            setSetting(s);
            setLoading(false);
        });
        return () => {
            mounted = false;
            listeners.delete(listener);
        };
    }, []);

    return { setting, loading };
};
